<template>
  <div class="rounded-lg overflow-hidden box-border">
    <div class="scrollable-table-container" :style="tableStyle">
      <table
        class="w-full px-2 relative"
        style="border-collapse: separate; border-spacing: 0 16px"
      >
        <thead
          :style="{
            position: isPresentationMode ? 'relative' : 'sticky',
            top: isPresentationMode ? '' : '0',
            zIndex: isPresentationMode ? 'auto' : '1000',
            backgroundColor: isPresentationMode ? 'transparent' : '#f3f4f6',
            width: '100%',
          }"
          :class="{ 'shadow-custom': !isPresentationMode }"
        >
          <th
            v-for="header in visibleHeaders"
            :key="header.text"
            :class="[
              'p-4 text-xs sm:text-sm md:text-base',
              header.text === 'Nome' ? 'text-left' : 'text-center',
            ]"
            :style="{
              color: isPainelGeral ? '#6D6D6D' : '',
            }"
          >
            {{ header.text }}
          </th>
        </thead>
        <tbody class="bg-white">
          <tr
            v-for="(row, rowIndex) in data"
            :key="rowIndex"
            :class="[getRowClasses(rowIndex)]"
            class="rounded-lg"
          >
            <td
              class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
              :class="[getCellClasses(0, row)]"
              :style="{
                color: isPainelGeral ? '#434343' : '',
              }"
              v-if="isColumnVisible"
            >
              <div class="flex items-center justify-center gap-2">
                <span>{{ row.top }}</span>

                <template v-if="typeof row.positionChange !== 'undefined'">
                  <template v-if="row.positionChange > 0">
                    <ArrowUpIcon class="icon" style="color: #12cc68" />
                  </template>
                  <template v-else-if="row.positionChange < 0">
                    <ArrowDownIcon class="icon" style="color: #c8283c" />
                  </template>
                  <template v-else>
                    <MinusIcon class="icon" style="color: #6d6d6d" />
                  </template>
                </template>
              </div>
            </td>

            <template>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-left"
              >
                <div class="flex items-center justify-start gap-4 py-1 px-2">
                  <span
                    :style="{
                      ...(row.dataInativacao ? { color: '#dc3545' } : {}),
                      ...(!isPresentationMode && isPainelTopSupervisores
                        ? getInadPrimeiraParcelaStyle(row.totalVendas)
                        : {}),
                    }"
                    @click="
                      !isPresentationMode && emitOpenModalConsultants(row)
                    "
                  >
                    {{ row.nome }}
                  </span>

                  <div
                    style="background: #dc3545"
                    class="py-1 px-2 rounded-lg text-white text-xs"
                    v-if="row.dataInativacao"
                  >
                    Inativo
                  </div>
                  <div class="py-1 px-2 text-white" v-else>Ativo</div>

                  <div
                    style="background: #0c90cf"
                    class="py-1 px-2 rounded-lg text-white text-xs"
                    v-if="row.nomeSupervisor"
                  >
                    {{ row.nomeSupervisor }}
                  </div>
                </div>
              </td>
            </template>

            <template>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
                :style="
                  !isPresentationMode
                    ? getInadPrimeiraParcelaStyle(row.totalVendas)
                    : {}
                "
                @click="
                  !isPresentationMode &&
                    row.totalVendas > 0 &&
                    emitOpenModal(row, false)
                "
              >
                {{ row.totalVendas }}
              </td>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
                :style="
                  !isPresentationMode
                    ? getInadPrimeiraParcelaStyle(row.cancelados)
                    : {}
                "
                @click="
                  !isPresentationMode &&
                    row.cancelados > 0 &&
                    emitOpenCanceledModal(row, false)
                "
              >
                {{ row.cancelados }}
              </td>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
                :style="
                  !isPresentationMode
                    ? getInadPrimeiraParcelaStyle(row.inadPrimeiraParcela)
                    : {}
                "
                @click="
                  !isPresentationMode &&
                    row.inadPrimeiraParcela > 0 &&
                    emitOpenModal(row)
                "
              >
                {{ row.inadPrimeiraParcela }}
              </td>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
              >
                <div class="flex items-center justify-center gap-1">
                  <span>{{ row.relogio ? row.relogio : 0 }}</span>
                  <span class="text-xxs"
                    >({{
                      calculatePercentage(row.totalVendas, row.relogio)
                    }}%)</span
                  >
                </div>
              </td>

              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
              >
                {{ row.diferencaRelogio }}
              </td>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
              >
                {{ row.metaTotal }}
              </td>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
              >
                {{ row.diferencaMeta }}
              </td>
              <td
                v-if="
                  $can('Visualizar Valor da Primeira Parcela', 'Super Ranking')
                "
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
              >
                {{ row.valorPrimeiraParcela }}
              </td>
              <td
                v-if="$can('Visualizar Projeções Futuras', 'Super Ranking')"
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
              >
                {{ row.somaMensalidade }}
              </td>
              <td
                class="px-6 py-4 label text-xs sm:text-sm md:text-base text-center"
                v-if="row.performance"
              >
                <template v-if="row.performance === 'good'">
                  <SmileIcon class="icon" style="color: #12cc68" />
                </template>
                <template v-else-if="row.performance === 'meh'">
                  <MehIcon class="icon" style="color: #e6b932" />
                </template>
                <template v-else-if="row.performance === 'bad'">
                  <FrownIcon class="icon" style="color: #c8283c" />
                </template>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  ArrowDownIcon,
  ArrowUpIcon,
  MinusIcon,
  SmileIcon,
  MehIcon,
  FrownIcon,
} from 'vue-feather-icons';

export default {
  name: 'RankingTableV2',
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [],
    },
    rankingField: {
      type: String,
      required: true,
    },
    orderDescending: {
      type: Boolean,
      default: true,
    },
    isTopRed: {
      type: Boolean,
      default: false,
    },
    isPainelGeral: {
      type: Boolean,
      default: false,
    },
    isPresentationMode: {
      type: Boolean,
      default: false,
    },
    isPainelTopSupervisores: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ArrowDownIcon,
    ArrowUpIcon,
    MinusIcon,
    SmileIcon,
    MehIcon,
    FrownIcon,
  },
  computed: {
    calculatePercentage() {
      return (totalVendas, relogio) => {
        if (relogio === 0) {
          return '0.00';
        }
        const percentage = (totalVendas / relogio) * 100;
        return isFinite(percentage) ? percentage.toFixed(2) : '0.00';
      };
    },
    visibleHeaders() {
      const HEADER_PERMISSION = [
        {
          text: 'Valor 1⁠ª Parcela',
          permission: 'Visualizar Valor da Primeira Parcela',
        },
        {
          text: 'Projeções Futuras',
          permission: 'Visualizar Projeções Futuras',
        },
      ];
      const filteredHeaders =
        this.isPainelGeral || !this.hasTopData()
          ? this.headers.filter((header) => header.visible !== 'none')
          : this.headers;

      return filteredHeaders.filter((header) => {
        const headerPermission = HEADER_PERMISSION.find(
          (hp) => hp.text === header.text,
        );
        return headerPermission
          ? this.$can(headerPermission.permission, 'Super Ranking')
          : true;
      });
    },
    isColumnVisible() {
      const isVisible = !this.isPainelGeral && this.hasTopData();
      if (isVisible) {
        return isVisible;
      }

      const TOP_COLUMN_NAME = 'Top';
      const header = this.headers.find(({ text }) => text === TOP_COLUMN_NAME);
      return header ? header.visible !== 'none' : false;
    },
    tableStyle() {
      const styles = {
        maxHeight: '100vh',
        overflowY: 'auto',
      };

      if (this.isPresentationMode) {
        styles.maxHeight = 'none';
        styles.overflowY = 'hidden';
      }

      if (this.isPainelGeral && !this.isPresentationMode) {
        styles.maxHeight = '25rem';
      }

      return styles;
    },
  },
  methods: {
    getCellClasses(index, row) {
      return [
        'font-medium text-gray-900 px-6 py-4 whitespace-nowrap',
        index === 0 ? 'rounded-tl-lg rounded-bl-lg' : '',
        index === Object.keys(row).length - 1
          ? 'rounded-tr-lg rounded-br-lg'
          : '',
      ];
    },
    getRowClasses(rowIndex) {
      if (this.isPainelGeral) {
        return '';
      }
      return [
        rowIndex === this.data.length - 1 ? 'rounded-b-lg' : '',
        'shadow-custom bg-white',
      ];
    },
    getInadPrimeiraParcelaStyle(value) {
      if (value > 0) {
        return {
          color: '#0F4173',
          fontFamily: 'Inter',
          fontWeight: '600',

          cursor: 'pointer',
        };
      }
      return {};
    },
    emitOpenModal(row, onlyOpen = true) {
      this.$emit('openModal', row, onlyOpen);
    },
    emitOpenCanceledModal(row) {
      this.$emit('openCanceledModal', row);
    },
    emitOpenModalConsultants(row) {
      this.$emit('openModalConsultants', row);
    },
    hasTopData() {
      const hasTop = this.data.find(({ top }) => top);
      return hasTop;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap');

.bg-green-success {
  background-color: #e8faf0;
}
.shadow-custom {
  box-shadow: -5px 0 5px 0 rgba(0, 0, 0, 0.05), 5px 0 5px 0 rgba(0, 0, 0, 0.05),
    0 -5px 5px 0 rgba(0, 0, 0, 0.05), 0 5px 5px 0 rgba(0, 0, 0, 0.05);
}
.red-error-10 {
  background-color: #faeaec;
}
.title {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-style: normal;
  color: #6d6d6d;
  letter-spacing: 0.15px;
  font-size: 0.75rem; /* Menor tamanho para mobile */
}
.label {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 1rem;
  letter-spacing: 0.25px;
  color: #434343;
}
.text-xxs {
  font-size: 0.625rem;
  line-height: 1rem;
}
.scrollable-tbody {
  padding-top: 50px;
}
.scrollable-table-container {
  display: block;
  max-height: 100vh;
  overflow-y: auto;
  scrollbar-color: #114880 transparent;
  scrollbar-width: thin;
}
.scrollable-table-container::-webkit-scrollbar {
  width: 50px;
  height: 50px;
}
.scrollable-table-container::-webkit-scrollbar-track {
  background: transparent;
}
.scrollable-table-container::-webkit-scrollbar-thumb {
  background: #114880;
  border-radius: 8px;
  border: 23px solid transparent;
  background-clip: padding-box;
}
</style>
